/**
 * useWalletBalance — tiny module-scoped cache in front of GET /api/doorbell/wallet.
 *
 * Rendered on every alert card via `LiveRoomLauncher` (for the "Funds
 * remaining" chip that nudges a top-up before the reporter hits Start
 * and gets a 402). A dashboard showing 20 alert cards would otherwise
 * fire 20 identical wallet requests on mount — this cache dedupes them
 * into a single fetch per 20-second window.
 *
 * Also refreshes automatically when a WebSocket event lands that
 * changed the balance (`doorbell_end` with a refund, `live_room_ended`
 * with a refund, or any `wallet_topup_completed`) so the chip stays in
 * sync without polling.
 *
 * API: `const { balance, loading, refetch } = useWalletBalance(enabled);`
 * `enabled = false` short-circuits the fetch entirely — pass false when
 * the caller has no need for the balance (non-creator, resolved alert, etc.).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Stale-while-revalidate window — every mount inside this window reuses
// the last successful response. 20s is short enough that a top-up shows
// up within one card render, long enough that a 20-card dashboard fires
// a single request.
const CACHE_TTL_MS = 20_000;

let cached = { balance: null, at: 0 };
let inflight = null;             // dedupe concurrent fetches
const listeners = new Set();     // components subscribing to updates

function notify() {
  listeners.forEach((fn) => { try { fn(cached.balance); } catch { /* noop */ } });
}

async function fetchOnce() {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await axios.get(`${API}/doorbell/wallet`, { withCredentials: true });
      cached = { balance: r.data?.balance_ngn ?? 0, at: Date.now() };
      notify();
      return cached.balance;
    } catch {
      // Silent — a wallet fetch failure just means the chip won't show;
      // no user-visible error is needed.
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Force-refetch from all callers (e.g. after a top-up completes). */
export function refreshWalletBalance() {
  cached.at = 0;
  return fetchOnce();
}

export default function useWalletBalance(enabled = true) {
  const [balance, setBalance] = useState(cached.balance);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    await fetchOnce();
    setBalance(cached.balance);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const listener = (b) => setBalance(b);
    listeners.add(listener);
    const fresh = Date.now() - cached.at < CACHE_TTL_MS;
    if (fresh) {
      setBalance(cached.balance);
    } else {
      setLoading(true);
      fetchOnce().then((b) => { setBalance(b); setLoading(false); });
    }
    return () => { listeners.delete(listener); };
  }, [enabled]);

  return { balance, loading, refetch };
}
